import { useCallback } from "react";
import { useCurrentUser, useUpdateMyPreferences } from "../networking/user.hooks";

export interface BetaFeature {
	/** Stable storage key under `profile.preferences.betaFeatures`. Never rename. */
	key: string;
	label: string;
	description: string;
	/** State for a user who has not flipped it. Beta features are opt-in, so: off. */
	defaultEnabled?: boolean;
}

/**
 * The beta features a dashboard user may flip for themselves, in the order they
 * are listed on Inställningar → Avancerat → Beta-funktionalitet.
 *
 * The choice is stored on the user's own profile (`profile.preferences`), not in
 * this browser, so it follows the user between devices and affects nobody else.
 * These flags are the *only* thing gating the features they name — there is no
 * organisation flag and no role scope behind them — so a user who switches one
 * on gets the real feature, not a broken entry point.
 */
export const BETA_FEATURES: BetaFeature[] = [
	{
		key: "aiAssistant",
		label: "AI-assistent",
		description:
			"Visar AI-assistenten i sidomenyn (⌘J / Ctrl+J) samt rapportbyggaren. Assistenten är under utveckling och kan svara fel — dubbelkolla alltid siffror innan du agerar på dem.",
	},
];

/** The user's explicit choice for a feature, or undefined when they have none. */
export function useBetaFeatureChoice(key: string): boolean | undefined {
	const { data: currentUser } = useCurrentUser();
	return currentUser?.profile?.preferences?.betaFeatures?.[key];
}

/**
 * Whether a beta feature is on for the current user: their own choice when they
 * have made one, otherwise the feature's default (off).
 */
export function useBetaFeatureEnabled(key: string): boolean {
	const choice = useBetaFeatureChoice(key);
	return choice ?? !!BETA_FEATURES.find((f) => f.key === key)?.defaultEnabled;
}

/**
 * Persist (or clear, by passing undefined) the user's choice for one beta
 * feature. The whole map is written back since preferences merge one level deep
 * server-side.
 */
export function useSetBetaFeature() {
	const { data: currentUser } = useCurrentUser();
	const { mutate: updatePreferences } = useUpdateMyPreferences();
	const stored = currentUser?.profile?.preferences?.betaFeatures;

	return useCallback(
		(key: string, enabled: boolean | undefined) => {
			const next = { ...stored };
			if (enabled === undefined) delete next[key];
			else next[key] = enabled;
			updatePreferences({ betaFeatures: next });
		},
		[stored, updatePreferences]
	);
}
